/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { BaseService } from './base.service';
import { Observable, lastValueFrom, map } from 'rxjs';
import { BusinessTree, Entity, Registration, Subsidiary } from '@app/types/global.type';

@Injectable({
  providedIn: 'root',
})
export class MgmtService extends BaseService {
  registrations: any[] = [];
  pans: any[] = [];
  gstins: any[] = [];
  registrationMap!: Map<string, Registration[]>;

  getEntityList(): Observable<Entity[]> {
    return this.http.get(`user/entity`).pipe(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map((resp: any) => {
        if (resp.status) return resp.response;
        this.toastr.error(resp.message ?? 'Error');
        return null;
      }),
    );
  }

  getGstinList(userId: string | undefined, rootCmpId: string | undefined) {
    const paramsData = {
      companyId: rootCmpId as string,
      userMailId: userId as string,
    };
    return (
      this.http
        // .get(`user/company/assignedGstinsDtls?companyId=${rootCmpId}&userMailId=${userId}`)
        .get(`user/company/assignedGstinsDtls`, { params: paramsData })
        .pipe(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          map((resp: any) => {
            if (resp.status) return resp.response;
            this.toastr.error(resp.message ?? 'Error');
            return null;
          }),
        )
    );
  }

  checkSubscription(params: { pan: string }) {
    return this.http.get(`subscription/active`, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  changeCompany(params: { companyid: string }) {
    return this.http.post(`user/changecompany`, {}, { params }).pipe(
      map((resp: any) => {
        if (resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  getBusinessHierarchy(params: { companyid: string }) {
    return this.http.get(`company/businesshierarchy`, { params }).pipe(
      map((resp: any) => {
        if (resp && resp.status) return resp;
        // this.toastError(resp.message);
        return null;
      }),
    );
  }

  resetRegistrations() {
    this.registrations = [];
    this.pans = [];
    this.gstins = [];
    this.registrationMap = new Map<string, Registration[]>();
  }

  async getRegistrations(companyid: string): Promise<Map<string, Registration[]>> {
    if (this.registrationMap && this.registrationMap.size > 0)
      return this.registrationMap;
    await this._getCompanies(companyid);
    return this.registrationMap;
  }

  private async _flatCompanies(parentPan: string, data: Subsidiary[]) {
    data?.forEach((company) => {
      if (company.entityType === 'POB') return;

      const children = company.childCompanies;
      if (children?.length && company.entityType === 'LEGAL')
        this._flatCompanies(company.pan!, children);

      // let stateCode: string | undefined;
      // if (company.entityType === 'FILING') {
      //   stateCode = company.gstin?.slice(0, 1);
      // }

      this.registrations.push({
        root: false,
        type:
          company.entityType === 'FILING' || company.entityType === 'SEZ'
            ? 'GSTIN'
            : 'PAN',
        entityType: company.entityType,
        name: company.companyName,
        companyId: company.companyId,
        parentCompanyId: Number(company.parentCompanyId),
        parentPan: parentPan,
        pan: company.pan,
        gstin: company.gstin,
        // state: stateCode ? stateCodeMap[stateCode]?.stateName : null,
        registeredDate: company.registartionDate,
      });
    });
  }

  private async _getCompanies(companyId: string) {
    const data = this.getBusinessHierarchy({ companyid: companyId });
    const result = await lastValueFrom(data);
    const businessTree = result.response as BusinessTree;

    this.registrations = [];
    this.pans = [];
    this.gstins = [];

    this.registrations.push({
      root: true,
      type: 'PAN',
      entityType: businessTree.entityType,
      name: businessTree.companyName,
      companyId: businessTree.companyId,
      parentCompanyId: Number(businessTree.parentCompanyId),
      pan: businessTree.pan,
    });

    await this._flatCompanies(businessTree.pan, businessTree.childCompanies);
    await this.groupRegistrationsByPan(this.registrations);

    this.registrations.forEach((business) => {
      if (business.type === 'GSTIN') this.gstins.push(business);
      else if (business.type === 'PAN') this.pans.push(business);
    });
  }

  async groupRegistrationsByPan(registrations: Registration[]) {
    const map = new Map<string, Registration[]>();

    for (const reg of registrations) {
      if (reg.type === 'PAN') {
        const panKey = reg.pan;

        const related: Registration[] = [reg];

        const children = registrations.filter(
          (r) => r.type === 'GSTIN' && r.parentPan === panKey,
        );

        related.push(...children);

        map.set(panKey, related);
      }
    }

    this.registrationMap = map;
  }

  async getRegistrationsPanList() {
    return this.pans;
  }

  async getRegistrationsGstinList() {
    return this.gstins;
  }
}
